import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { CardSetTransformationService, CoreService, DialogResult, DialogService, FormNavigationService, SaveDataRequest, SpinnerService } from 'dspx-core';
import { BaseCardSetDetailComponent } from '../base-card-set-detail-component/base-card-set-detail.component';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { ErrorService } from '../../services/error.service';
import { AppDataService } from '../../services/app-data.service';
import { HtmlHelperService } from '../../services/html-helper.service';
import { FormDataService, HideableGrid } from '../../services/form-data.service';
import { QuestionPath, ResponseParserService } from '../../services/response-parser.service';
import { API_ENDPOINT_KEYS, API_ENDPOINTS } from '../../constants/api-endpoints.constants';
import { TEMPLATES, VERSIONS } from '../../constants/templates.constants';
import { APP_DATA_KEYS, FORM_ACTIONS, HIDEABLE_GRIDS, NAV_TAB_NUMBERS, OPTION_SET_CODES, PAGE_NUMBERS, POP_UP_CODES, QUESTION_CODES, READ_ONLY_ACCESS_PAGE_ACTION_BUTTONS, SECTION_CODES, SERVER_ERROR_POP_UP_CODES } from '../../constants/app-data.constants';
import { ROUTE_CONSTANTS } from '../../constants/route.constants';
import { take } from 'rxjs';

@Component({
  selector: 'app-approver-dopi-form',
  templateUrl: './approver-dopi-form.component.html',
  styleUrl: './approver-dopi-form.component.scss',
  encapsulation: ViewEncapsulation.None
})
export class ApproverDopiFormComponent extends BaseCardSetDetailComponent implements OnInit, OnDestroy {

  fieldTypeReplacements = CardSetTransformationService.cardSetWithFlatTabsFieldTypeReplacements;
  id = '';
  formServiceEndpoint: string = null;
  templateId: number = TEMPLATES.approverDopiForm;
  versionNo: number = VERSIONS.latest;
  activeDialog: string;
  editing = false;
  isViewRoute = false;
  lastUpdatedTime: string;

  private versionHistoryHideableGrid: HideableGrid;
  private isReturnableForActionWithoutAssessment: boolean;

  constructor(
    protected router: Router,
    protected appDataService: AppDataService,
    protected coreService: CoreService,
    protected formDataService: FormDataService,
    protected htmlHelperService: HtmlHelperService,
    protected dialogService: DialogService,
    private route: ActivatedRoute,
    private errorService: ErrorService,
    private responseParserService: ResponseParserService,
    private spinnerService: SpinnerService,
    private formNavigationService: FormNavigationService) {
    super(router, appDataService, coreService, formDataService, htmlHelperService, dialogService);
  }

  ngOnDestroy(): void {
    this.unsubscribeSubscriptions();
    this.coreService.applicationFunctions = {};
    this.coreService.repeatingPageFunctions = {};
    this.coreService.repeatingSectionFunctions = {};
  }

  ngOnInit(): void {
    this.registerSubscription(
      this.route.paramMap.subscribe((paramMap: ParamMap) => {
        const id = paramMap.get('id');
        if (id) {
          this.id = id;
          this.isViewRoute = this.router.url === `/${ROUTE_CONSTANTS.approverDopiForm.viewPrefix}/${id}`;
          this.formServiceEndpoint = this.isViewRoute
            ? API_ENDPOINT_KEYS.getApproverDopiFormConfigAndResponseReadOnly
            : API_ENDPOINT_KEYS.getApproverDopiFormConfigAndResponse;
          this.editing = true;
        }
        else {
          this.resetDirtyFlagAfterLoading();
        }
      })
    );

    this.formNavigationService.onNavigation().subscribe(event => {
      setTimeout(() => {
        this.overrideHtmlTitleSetByDspxFramework();
      })
    });

    this.coreService.conditionFunctions['isEditing'] = () => this.editing;
    this.coreService.conditionFunctions['areMandatoryFieldsEntered'] = () => this.areMandatoryFieldsEntered();

    const canApprove = () => this.isFormEditable && this.hasConsent();
    const canReturnForAction = () => this.isFormEditable && !this.hasConsent();

    this.coreService.applicationFunctions['canApprove'] = () => canApprove();
    this.coreService.applicationFunctions['canReturnForAction'] = () => canReturnForAction();
    this.coreService.applicationFunctions['canReturnForActionWithoutAssessment'] = () => !canReturnForAction() && this.isReturnableForActionWithoutAssessment;
    this.coreService.applicationFunctions['canSave'] = () => this.isFormEditable;
    this.coreService.applicationFunctions['canSwitchToEdit'] = () => this.isViewRoute;

    this.coreService.applicationFunctions['approve'] = () => this.approve(null);
    this.coreService.applicationFunctions['returnForAction'] = () => this.returnForAction(null);
    this.coreService.applicationFunctions['returnForActionWithoutAssessment'] = () => this.returnForActionWithoutAssessment(null);
    this.coreService.applicationFunctions['switchToEdit'] = () => this.switchToEdit(null);
    this.coreService.applicationFunctions['downloadCopy'] = () => this.downloadCopy();

    this.versionHistoryHideableGrid = this.initialiseHideableGrid(HIDEABLE_GRIDS.approverDopiForm.versionHistory);
  }

  formLoaded(event: any): void {
    if (event.retryAsReadOnly) {
      this.navigate(['/', ROUTE_CONSTANTS.approverDopiForm.viewPrefix, this.id], true);
      return;
    }

    this.isFormEditable = !!event?.isFormEditable;
    this.lastUpdatedTime = event.lastUpdatedTime;
    this.isReturnableForActionWithoutAssessment = !!event?.isReturnableForActionWithoutAssessment;

    if (this.baseForm && this.baseForm.model) {
      this.appDataService.setAppData(APP_DATA_KEYS.previousModel, JSON.parse(JSON.stringify(this.baseForm.model)));
    }

    this.versionHistoryHideableGrid.toggle(HIDEABLE_GRIDS.approverDopiForm.versionHistory.showOnFirstLoad);

    this.ensurePageActionButtonsAreEnabledOnFormLoad(READ_ONLY_ACCESS_PAGE_ACTION_BUTTONS.approverDopiForm);
    this.handlePostFormLoadActions(event);
  }

  modelChanged(event: any): void {
    this.handleSingleCheckBoxChanges();
  }

  switchToEdit(event: any): void {
    this.navigate(['/', ROUTE_CONSTANTS.approverDopiForm.prefix, this.id], true);
  }

  save(event: any): void {
    this.executeSave({
      skipValidation: true,
      apiEndpoint: API_ENDPOINTS.approverDopiForm.save,
      confirmationPopupCode: null,
      serverErrorPopupCodes: SERVER_ERROR_POP_UP_CODES.saveApproverDopiForm,
      successPopupCode: POP_UP_CODES.common.saveSuccessful
    });
  }

  approve(event: any): void {
    this.executeSave({
      skipValidation: false,
      apiEndpoint: API_ENDPOINTS.approverDopiForm.approve,
      confirmationPopupCode: POP_UP_CODES.approverDopiForm.approveConfirmation,
      serverErrorPopupCodes: SERVER_ERROR_POP_UP_CODES.saveApproverDopiForm,
      successPopupCode: POP_UP_CODES.approverDopiForm.approveSuccessful
    });
  }

  returnForAction(event: any): void {
    this.executeSave({
      skipValidation: false,
      apiEndpoint: API_ENDPOINTS.approverDopiForm.returnForAction,
      confirmationPopupCode: POP_UP_CODES.approverDopiForm.returnForActionConfirmation,
      serverErrorPopupCodes: SERVER_ERROR_POP_UP_CODES.saveApproverDopiForm,
      successPopupCode: POP_UP_CODES.approverDopiForm.returnedForAction
    });
  }

  returnForActionWithoutAssessment(event: any): void {
    this.executeSave({
      skipValidation: true,
      apiEndpoint: API_ENDPOINTS.approverDopiForm.returnForActionWithoutAssessment,
      confirmationPopupCode: POP_UP_CODES.approverDopiForm.returnForActionConfirmation,
      serverErrorPopupCodes: SERVER_ERROR_POP_UP_CODES.saveApproverDopiForm,
      successPopupCode: POP_UP_CODES.approverDopiForm.returnedForAction
    });
  }

  private executeSave(parameters: { skipValidation: boolean, apiEndpoint: string, confirmationPopupCode: string, serverErrorPopupCodes: string[], successPopupCode: string }): void {
    if (!parameters.skipValidation && !this.baseForm.fields[0]?.formControl.valid) {
      this.baseForm.markAllFieldsAsTouched();
      this.baseForm.showValidationSummary(true);
      this.dialogService.showDialog(POP_UP_CODES.common.validationError);
    }
    else {
      const saveData = () => {
        const saveDataRequest: SaveDataRequest = {
          template: this.templateId,
          versionNumber: this.versionNo,
          id: this.id,
          response: JSON.stringify(this.baseForm.model),
          expectedLastUpdateTime: this.lastUpdatedTime,
          isSubmit: false // not used
        }
        this.spinnerService.showSpinner();
        this.registerSubscription(
          this.appDataService.post(parameters.apiEndpoint, saveDataRequest)
            .pipe(take(1), this.errorService.errorHandler())
            .subscribe(data => {
              this.spinnerService.hideSpinner();
              this.activeDialog = parameters.serverErrorPopupCodes.includes(data.message)
                ? data.message
                : parameters.successPopupCode;
              this.dialogService.showDialog(this.activeDialog);
            }));
      };
      if (!parameters.confirmationPopupCode) {
        saveData();
      }
      else {
        this.registerSubscription(
          this.dialogService
            .showConfirm(parameters.confirmationPopupCode)
            .pipe(take(1))
            .subscribe(data => {
              switch (data as DialogResult) {
                case DialogResult.ACCEPTED: {
                  saveData();
                  break;
                }
                case DialogResult.REJECTED: {
                  break;
                }
              }
            })
        );
      }
    }
  }

  cancel(event: any) {
    this.navigateBackToDashboard();
  }

  dialogSubmitted(event: any): void {

    const activeDialog = this.activeDialog;
    delete this.activeDialog;

    switch (activeDialog) {
      case POP_UP_CODES.common.saveSuccessful:
      case POP_UP_CODES.approverDopiForm.approveSuccessful:
      case POP_UP_CODES.approverDopiForm.returnedForAction: {
        this.navigateBackToDashboard(true);
        break;
      }
    }
  }

  actionButtonClicked(event: any): void {
    switch (event.field.key) {
      case QUESTION_CODES.approverDopiForm.versionHistory: {
        switch (event.event.type) {
          case FORM_ACTIONS.approverDopiForm.downloadVersion: {
            this.downloadVersion(event.event.data.id);
            break;
          }
        }
        break;
      }
    }
  }

  private navigateBackToDashboard(ignoreUnsavedChanges = false): void {
    this.navigate(['/', ROUTE_CONSTANTS.dopiForm.list], ignoreUnsavedChanges);
  }

  private downloadCopy(): void {
    const downloadCopyEndpoint = `${API_ENDPOINTS.dopiForm.downloadCopy}/${this.id}`;
    this.appDataService.generatePdf(downloadCopyEndpoint);
  }

  private downloadVersion(versionId: string): void {
    const downloadVersionEndpoint = `${API_ENDPOINTS.dopiForm.downloadVersion}/${this.id}/${versionId}`
    this.appDataService.generatePdf(downloadVersionEndpoint);
  }

  private handleSingleCheckBoxChanges(): void {
    const consentQuestionPath: QuestionPath = [
      NAV_TAB_NUMBERS.approverDopiForm.navTabPlaceholder,
      PAGE_NUMBERS.approverDopiForm.pagePlaceholder,
      SECTION_CODES.approverDopiForm.dopiFormApproverReviewSection,
      QUESTION_CODES.approverDopiForm.consent
    ];
    this.responseParserService.handleSingleCheckBoxQuestionChanges(
      () => this.baseForm.model,
      model => this.baseForm.model = model,
      [consentQuestionPath]);
  }

  private hasConsent(): boolean {
    const consentModel = this.responseParserService.findQuestionModel(
      this.baseForm?.model,
      NAV_TAB_NUMBERS.approverDopiForm.navTabPlaceholder,
      PAGE_NUMBERS.approverDopiForm.pagePlaceholder,
      null,
      SECTION_CODES.approverDopiForm.dopiFormApproverReviewSection,
      null,
      QUESTION_CODES.approverDopiForm.consent);
    
    return consentModel === true;
  }

  private getApproverCommentsQuestionModel(): string {
    return this.responseParserService.findQuestionModel(
      this.baseForm?.model,
      NAV_TAB_NUMBERS.approverDopiForm.navTabPlaceholder,
      PAGE_NUMBERS.approverDopiForm.pagePlaceholder,
      null,
      SECTION_CODES.approverDopiForm.dopiFormApproverReviewSection,
      null,
      QUESTION_CODES.approverDopiForm.approverComments);
  }

  private getApproverAssessmentCoiFlagModel(): string {
    return this.responseParserService.findQuestionModel(
      this.baseForm?.model,
      NAV_TAB_NUMBERS.approverDopiForm.navTabPlaceholder,
      PAGE_NUMBERS.approverDopiForm.pagePlaceholder,
      null,
      SECTION_CODES.approverDopiForm.dopiFormApproverReviewSection,
      null,
      QUESTION_CODES.approverDopiForm.approverAssessmentCoiFlag);
  }

  private getHasDeclarerConflictOfInterestModel(): string {
    return this.responseParserService.findQuestionModel(
      this.baseForm?.model,
      NAV_TAB_NUMBERS.approverDopiForm.navTabPlaceholder,
      PAGE_NUMBERS.approverDopiForm.pagePlaceholder,
      null,
      SECTION_CODES.approverDopiForm.dopiFormApproverReviewSection,
      null,
      QUESTION_CODES.approverDopiForm.hasDeclarerConflictOfInterest);
  }

  private areMandatoryFieldsEntered(): boolean {
    const comment = this.getApproverCommentsQuestionModel();
    const approverAssessmentCoiFlag = this.getApproverAssessmentCoiFlagModel();
    const hasDeclarerConflictOfInterest = this.getHasDeclarerConflictOfInterestModel();
    const hasAnsweredCoiQuestions = (hasDeclarerConflictOfInterest === OPTION_SET_CODES.approverDopiForm.yesNoMoreInformationNeeded.yes && approverAssessmentCoiFlag) ||
          hasDeclarerConflictOfInterest === OPTION_SET_CODES.approverDopiForm.yesNoMoreInformationNeeded.no;
    
    if(comment && hasAnsweredCoiQuestions) {
      return true;
    }

    return false;
  }
}
